import React from "react"
import id from "@irati/utils/id"
import {StatementItem, TransactionType} from "@irati/types"
import StatementItemTypes from "@irati/wallet/types/enums/StatementItemTypes"

import enumToArray from "@irati/utils/enumToArray"
import BulkRewardTransfer from "./BulkRewardTransfer"
import ManualPointsTransfer from "./ManualPointsTransfer"
import RefundFromRewardsWallet from "./RefundFromRewardsWallet"
import RewardPointsRewarded from "./RewardPointsRewarded"
import RewardPointsTransfer from "./RewardPointsTransfer"
import SendCredit from "./SendCredit"
import SendCreditAsReward from "./SendCreditAsReward"
import SendCreditAsRewardRefund from "./SendCreditAsRewardRefund"
import PerksGoCashback from "./PerksGoCashback"
import Spend from "./Spend"
import TopUp from "./TopUp"
import TransferToRewardsWallet from "./TransferToRewardsWallet"
import UseCreditToBuyReward from "./UseCreditToBuyReward"
import {SimpleStatement} from "./SimpleStatement"
import {CashbackTransfer} from "./CashbackTransfer"

interface Props {
  className?: string
  statementItem: StatementItem
  id?: string
  isRewardWallet?: boolean
  isTenantWallet?: boolean
  customDetails?: JSX.Element
}

const StatementItemClass = (props: Props) => {
  const {className, statementItem, isRewardWallet, isTenantWallet, customDetails} = props

  const {
    transfer: {type},
    content
  } = statementItem

  if (content && enumToArray(StatementItemTypes).includes(content.type)) {
    return <SimpleStatement statementItem={statementItem} customDetails={customDetails} />
  }

  switch (type) {
    case StatementItemTypes.SPEND: {
      const idNamespace = id("spend", props)
      return <Spend id={idNamespace} className={className} statementItem={statementItem} />
    }
    case StatementItemTypes.SEND_CREDIT_AS_REWARD: {
      const idNamespace = id("send-credit-as-reward", props)
      return <SendCreditAsReward id={idNamespace} className={className} isRewardWallet={isRewardWallet} statementItem={statementItem} />
    }
    case StatementItemTypes.PERKS_GO_CASHBACK: {
      return <PerksGoCashback className={className} statementItem={statementItem} />
    }
    case StatementItemTypes.USE_CREDIT_TO_BUY_REWARD: {
      const idNamespace = id("use-credit-to-buy-reward", props)
      return <UseCreditToBuyReward id={idNamespace} className={className} statementItem={statementItem} />
    }
    case StatementItemTypes.TRANSFER_TO_REWARDS_WALLET: {
      const idNamespace = id("transfer-to-rewards-wallet", props)
      return (
        <TransferToRewardsWallet id={idNamespace} isTenantWallet={isTenantWallet} className={className} statementItem={statementItem} />
      )
    }
    case StatementItemTypes.NEW_TOP_UP_BANK_TRANSFER:
    case StatementItemTypes.TOP_UP_BANK_TRANSFER: {
      const idNamespace = id("topup-bank-transfer", props)
      return <TopUp id={idNamespace} isBankTransfer className={className} statementItem={statementItem} />
    }
    case StatementItemTypes.NEW_TOP_UP_CARD_PAYMENT:
    case StatementItemTypes.TOP_UP_CARD_PAYMENT: {
      const idNamespace = id("topup-card-payment", props)
      return <TopUp id={idNamespace} className={className} statementItem={statementItem} />
    }
    case StatementItemTypes.REFUND_FROM_REWARDS_WALLET: {
      const idNamespace = id("refund-from-rewards-wallet", props)
      return (
        <RefundFromRewardsWallet id={idNamespace} isTenantWallet={isTenantWallet} className={className} statementItem={statementItem} />
      )
    }
    case StatementItemTypes.SEND_CREDIT: {
      const idNamespace = id("send-credit", props)
      return <SendCredit id={idNamespace} className={className} statementItem={statementItem} />
    }
    case StatementItemTypes.SEND_CREDIT_AS_REWARD_REFUND: {
      const idNamespace = id("send-credit-as-reward-refund", props)
      return (
        <SendCreditAsRewardRefund id={idNamespace} className={className} statementItem={statementItem} isRewardWallet={isRewardWallet} />
      )
    }
    case StatementItemTypes.TRANSFER: {
      return <RewardPointsTransfer className={className} statementItem={statementItem} />
    }
    case StatementItemTypes.REWARD: {
      if (statementItem.transactionType === TransactionType.BulkReward) {
        return <BulkRewardTransfer className={className} statementItem={statementItem} />
      }

      return <RewardPointsRewarded className={className} statementItem={statementItem} />
    }
    case StatementItemTypes.CASHBACK: {
      return <CashbackTransfer statementItem={statementItem} />
    }
    case StatementItemTypes.MANUAL_TRANSFER:
    default: {
      return <ManualPointsTransfer className={className} statementItem={statementItem} />
    }
  }
}

export default StatementItemClass
